import { useRef, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024;
const OK_EXT = ["pdf", "doc", "docx"];

export function ApplyForm({ role, onClose }) {
  const { t } = useLang();
  const f = t.careersForm;
  const fileRef = useRef(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cover_letter: "" });
  const [cv, setCv] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const fail = (msg) => { setError(msg); toast.error(msg); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!OK_EXT.includes(ext)) return fail(f.errType);
    if (file.size > MAX_BYTES) return fail(f.errSize);
    setError("");
    setCv(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !cv) return fail(f.err);
    setStatus("loading");
    setError("");
    const fd = new FormData();
    fd.append("role_slug", role.slug);
    fd.append("role_title", role.title);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("cv", cv);
    try {
      await axios.post(`${API}/careers/apply`, fd);
      setStatus("success");
      toast.success(f.toastOk);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setStatus("error");
      fail(typeof msg === "string" ? msg : f.toastFail);
    }
  };

  if (status === "success") {
    return (
      <div data-testid="careers-form-success" className="border border-[#222] p-8 lg:p-10 bg-[#0A0A0A]">
        <div className="flex items-center gap-3 text-[#C84C35]"><Check size={18} strokeWidth={1.5} /><span className="eyebrow text-[#C84C35]">{f.sentTitle}</span></div>
        <p className="mt-4 font-serif text-2xl lg:text-3xl leading-tight">{role.title}</p>
        <p className="mt-3 text-[#A19D94] leading-relaxed max-w-xl">{f.sentBody}</p>
        <button type="button" onClick={onClose} data-testid="careers-form-close" className="btn-ghost mt-8">{f.close}</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`careers-form-${role.slug}`} className="border border-[#222] p-8 lg:p-10 bg-[#0A0A0A] relative" noValidate>
      <button type="button" onClick={onClose} aria-label={f.close} data-testid="careers-form-dismiss" className="absolute top-6 right-6 text-[#66635C] hover:text-[#F4F1EB] transition-colors">
        <X size={18} strokeWidth={1.25} />
      </button>
      <div className="eyebrow">{f.applyingFor}</div>
      <div className="font-serif text-2xl lg:text-3xl mt-2 leading-tight pr-10">{role.title}</div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <label className="eyebrow">{f.name}</label>
          <input data-testid="careers-form-name" value={form.full_name} onChange={onChange("full_name")} placeholder={f.namePh} className="line-input mt-3" autoComplete="name" />
        </div>
        <div>
          <label className="eyebrow">{f.email}</label>
          <input data-testid="careers-form-email" type="email" value={form.email} onChange={onChange("email")} placeholder={f.emailPh} className="line-input mt-3" autoComplete="email" />
        </div>
        <div>
          <label className="eyebrow">{f.phone}</label>
          <input data-testid="careers-form-phone" value={form.phone} onChange={onChange("phone")} placeholder={f.phonePh} className="line-input mt-3" autoComplete="tel" />
        </div>
        <div>
          <label className="eyebrow">{f.cv}</label>
          <input ref={fileRef} data-testid="careers-form-cv" type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="sr-only" />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="careers-form-cv-button" className="line-input mt-3 flex items-center justify-between gap-4 text-left cursor-pointer">
            <span className={`truncate ${cv ? "text-[#F4F1EB]" : "text-[#66635C]"}`}>{cv ? cv.name : f.cvChoose}</span>
            <Paperclip size={16} strokeWidth={1.25} className="shrink-0 text-[#A19D94]" />
          </button>
          <div className="chapter-num text-[#66635C] mt-2">{cv ? f.cvChange : f.cvHint}</div>
        </div>
        <div className="md:col-span-2">
          <label className="eyebrow">{f.cover}</label>
          <textarea data-testid="careers-form-cover" rows={4} value={form.cover_letter} onChange={onChange("cover_letter")} placeholder={f.coverPh} className="line-input mt-3 resize-none" />
        </div>
      </div>

      {error && <p data-testid="careers-form-error" className="mt-6 text-sm text-[#C84C35]">{error}</p>}

      <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <p className="chapter-num text-[#66635C] max-w-md">{f.disclaimer}</p>
        <button type="submit" disabled={status === "loading"} data-testid="careers-form-submit" className="btn-primary disabled:opacity-60">
          {status === "loading" ? f.sending : f.submit}
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </button>
      </div>
    </form>
  );
}
